/**
 * WHICH ANSWERS MAY BE RECALLED WHERE, at each rung of the Answer Recall capability.
 *
 * Variables asked one question — may this block hold one. Recall asks two, because a recalled
 * answer has a SOURCE as well as a destination: may this block hold a recall OF THAT ANSWER.
 * Both ends widen together, which is what keeps the ladder one rung rather than a grid:
 *
 *   1 numerical   a numerical answer into a numerical question. Error carried forward, and the
 *                 answer equation that resolves it already exists.
 *   2 sensors     the value family at both ends. This is where FACETS arrive — a compass answer
 *                 is a pair and a GPS answer a point, so "which part of it" becomes a question.
 *   3 text        everything, and with it driven-by.
 *
 * GPS is deliberately NOT in rung 2, the same ruling variables makes: its numbers name a place
 * rather than a quantity, so carrying one forward moves where a student is sent.
 *
 * ## Hook-free, and on purpose
 *
 * Two callers need the same answer and only one is a component — the affordance that decides
 * whether to offer the control, and the issue check that decides whether a recall ALREADY THERE
 * is valid. The second exists because the first cannot be sufficient: a chip pasted into a block
 * the rung does not support is reconstituted by the extension's `parseHTML`, and lowering the
 * rung after authoring leaves every existing chip in place. Neither is grandfathering — the
 * stored data is identical either way, so the rule has to be about what is SUPPORTED.
 *
 * Recall has one fewer bypass than variables: a token is `[ara_<opaque id>]`, so a teacher
 * cannot type one. Paste, duplicate and a lowered rung are the routes that remain.
 */

/**
 * What each rung admits, at BOTH ends. The top rung is absent on purpose: it takes every block,
 * and listing them would be a second roster to keep in step with the block registry.
 */
export const RECALL_RUNG_BLOCKS: Record<number, readonly string[]> = {
  1: ['numerical'],
  2: ['numerical', 'slider', 'compass', 'tilt', 'rotation'],
};

/**
 * May an answer from `sourceType` be recalled into `destType`, at this rung?
 *
 * Fails CLOSED on a type no rung names, including one added after this table was written —
 * the opposite of how the affordance leans, and deliberately: the affordance protects a teacher
 * from a wrong "no", while this exists to refuse, and a stored block always knows its own type.
 */
export function recallAllowedBetween(
  rung: number, sourceType: string, destType: string,
): boolean {
  if (rung <= 0) return false;
  const allowed = RECALL_RUNG_BLOCKS[rung];
  // No list for this rung means the top one, which takes everything in both directions.
  if (!allowed) return true;
  return allowed.includes(sourceType) && allowed.includes(destType);
}

/**
 * The destination half alone — may this block hold a recall of ANY kind?
 *
 * What the insert affordance asks, because a control is offered before a source is chosen. The
 * source half is then applied to the offerable list rather than to the control, so a teacher at
 * rung 1 standing in a Numerical block sees the button and is offered only numerical answers.
 */
export function recallAllowedInBlock(rung: number, destType: string): boolean {
  if (rung <= 0) return false;
  const allowed = RECALL_RUNG_BLOCKS[rung];
  if (!allowed) return true;
  return allowed.includes(destType);
}
